import { html, PropertyValues, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { RadioButton } from '~/ui/components/radio';
import { GroupChangeEventDetail } from '~/ui/components/radio-group/types';
import { RadioChangeEventDetail } from '~/ui/components/radio/types';

import { translations as deTranslations } from './i18n/de-CH';
import { translations as frTranslations } from './i18n/fr-CH';

import { styles as globalStyles } from '../../scss/main.styles';
import { styles } from './radio-group.styles';
import DERFormElement from '~/ui/utils/der-form-element';
import { LocaleCode } from '~/common/interfaces';

type Translations = {
    [key: string]: { [key: string]: string };
};

const translations: Translations = {
    [LocaleCode.DE_CH]: deTranslations,
    [LocaleCode.FR_CH]: frTranslations,
};

export class RadioGroup extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: String }) language = LocaleCode.DE_CH;
    @property({ type: String }) name: string = '';
    @property({ type: String }) label: string = '';
    @property({ type: String, attribute: 'optional-label' }) optionalLabel: string = '(Optional)';
    @property({ type: String }) value: string = '';
    @property({ type: Boolean }) disabled: boolean = false;
    @property({ type: Boolean }) required: boolean = false;

    @query('slot') slotElement!: HTMLSlotElement;

    trans(key: string): string {
        const currentTranslations = translations[this.language] || translations[LocaleCode.DE_CH] || {};
        return currentTranslations[key] || key;
    }

    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    override connectedCallback(): void {
        super.connectedCallback();

        this.updateComplete.then(() => this.checkInputValidity());
    }

    override firstUpdated(): void {
        this.initChildRadios();
        setTimeout(() => {
            const checkedRadio = this.querySelector('der-ui-radio-button[checked]') as RadioButton;
            if (checkedRadio) {
                this.value = checkedRadio.value;
                this._updateRadioButtons();

                this._dispatchValueChangeEvent();
            }
        }, 0);
    }

    initChildRadios() {
        const radios = Array.from(this.slotElement.assignedElements()).filter(
            (el) => el.tagName === 'DER-UI-RADIO-BUTTON'
        );
        radios.forEach((radio) => radio.setAttribute('name', this.name));
    }

    protected override updated(changedProperties: PropertyValues): void {
        if (changedProperties.has('value')) {
            this._updateRadioButtons();
        }
    }

    private _updateRadioButtons(): void {
        const radioButtons = this.querySelectorAll('der-ui-radio-button');
        radioButtons.forEach((radio) => {
            if (radio.name === this.name) {
                radio.checked = radio.value === this.value;
            }
        });

        radioButtons.forEach((radio) => {
            if (radio.name === this.name) {
                radio.checked = radio.value === this.value;
            }
        });
    }

    private _handleRadioChange(e: CustomEvent<RadioChangeEventDetail>): void {
        e.stopPropagation();
        if (e.detail.name === this.name) {
            this.value = e.detail.value;
            this.checkInputValidity();
            // Update all radio buttons in the group
            this._updateRadioButtons();

            // Dispatch event for parent components
            this._dispatchValueChangeEvent();
        }
    }

    private _dispatchValueChangeEvent() {
        this._internals.setFormValue(this.value);
        this.dispatchEvent(
            new CustomEvent<GroupChangeEventDetail>('der.change', {
                bubbles: true,
                composed: true,
                detail: {
                    name: this.name,
                    value: this.value,
                },
            })
        );
    }

    private checkInputValidity() {
        if (!this.required) {
            return;
        }
        const checkedRadio = this.querySelector('der-ui-radio-button');
        if (checkedRadio) {
            this.setDERValidity({ valueMissing: !this.value }, this.trans('MISSING_VALUE'), checkedRadio.radioEl);
        }
    }

    override render() {
        return html`
            <div class="c-radio-group">
                <div class="c-radio-group__label-wrapper">
                    ${this.label ? html`<label class="c-radio-group__label">${this.label}</label>` : ''}
                    ${!this.required && !this.disabled
                        ? html` <span class="c-radio-group__optional-label"> ${this.optionalLabel}</span>`
                        : ''}
                </div>
                <div class="c-radio-group__radios" @der.change=${this._handleRadioChange}>
                    <slot></slot>
                </div>
            </div>
        `;
    }
}
